import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Layout from '../components/Layout';
import ExportModal from '../components/ExportModal';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  DollarSign, TrendingUp, Users, FileText, Download, 
  BarChart3, Settings, RefreshCw, Calendar, Euro,
  MessageSquare, Image, LayoutGrid, MessageCircle,
  Percent, Calculator, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BillingDashboard() {
  const { token, user } = useAuth();
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [profitSummary, setProfitSummary] = useState(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [clientBillingConfig, setClientBillingConfig] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [trend, setTrend] = useState([]);
  const [savingConfig, setSavingConfig] = useState(false);

  // Billing config form (Google cost + margin per category)
  const [billingForm, setBillingForm] = useState({
    basic: { google_cost: 0.005, margin_percent: 60 },
    single: { google_cost: 0.008, margin_percent: 50 },
    rich: { google_cost: 0.015, margin_percent: 66 },
    rich_media: { google_cost: 0.030, margin_percent: 50 },
    sms: { google_cost: 0.040, margin_percent: 37.5 }
  });

  function getCurrentMonth() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  // Calculate client price from Google cost and margin
  const calculateClientPrice = (googleCost, marginPercent) => {
    return googleCost * (1 + marginPercent / 100);
  };

  useEffect(() => {
    if (user?.role === 'superadmin') {
      fetchOverview();
      fetchProfitSummary();
      fetchInvoices();
      fetchClients();
      fetchTrend();
    }
  }, [token, user, selectedMonth]);

  // Using v2 API endpoints
  const fetchOverview = async () => {
    try {
      const response = await fetch(`${API_URL}/api/billing/dashboard/overview?month=${selectedMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOverview(data);
      }
    } catch (err) {
      console.error('Error fetching overview:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfitSummary = async () => {
    try {
      const response = await fetch(`${API_URL}/api/billing/profit-summary?month=${selectedMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProfitSummary(data);
      }
    } catch (err) {
      console.error('Error fetching profit summary:', err);
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/billing/invoices?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInvoices(data);
      }
    } catch (err) {
      console.error('Error fetching invoices:', err);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const fetchTrend = async () => {
    try {
      const response = await fetch(`${API_URL}/api/billing/dashboard/monthly-trend?months=6`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTrend(data.trend || []);
      }
    } catch (err) {
      console.error('Error fetching trend:', err);
    }
  };

  const fetchClientBillingConfig = async (clientId) => {
    try {
      const response = await fetch(`${API_URL}/api/billing/config/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClientBillingConfig(data);
        
        // Update form with client's current config
        const newForm = {};
        for (const [category, config] of Object.entries(data.billing_config)) {
          newForm[category] = {
            google_cost: config.google_cost,
            margin_percent: config.margin_percent
          };
        }
        setBillingForm(newForm);
      }
    } catch (err) {
      console.error('Error fetching billing config:', err);
    }
  };

  const handleClientSelect = (clientId) => {
    setSelectedClient(clientId);
    if (clientId) {
      fetchClientBillingConfig(clientId);
    } else {
      setClientBillingConfig(null);
    }
  };

  const handleSaveBillingConfig = async () => {
    if (!selectedClient) return;
    setSavingConfig(true);

    try {
      const response = await fetch(`${API_URL}/api/billing/config/${selectedClient}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(billingForm)
      });

      if (response.ok) {
        toast.success('Configuration de facturation mise à jour');
        fetchClientBillingConfig(selectedClient);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la mise à jour');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleGenerateInvoice = async (clientId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/billing/invoices/generate?month=${selectedMonth}&client_id=${clientId}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Facture générée');
        fetchInvoices();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la génération');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    }
  };

  const handleDownloadPDF = async (clientId, period) => {
    try {
      const response = await fetch(
        `${API_URL}/api/billing/invoices/${period}/pdf?client_id=${clientId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `facture_${period}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('PDF téléchargé');
      } else {
        toast.error('Erreur lors du téléchargement');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      basic: MessageCircle,
      single: MessageSquare,
      rich: Image,
      rich_media: LayoutGrid,
      sms: MessageSquare
    };
    const Icon = icons[category] || MessageSquare;
    return <Icon className="w-4 h-4" />;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      basic: 'Basic (OTP)',
      single: 'Single (Texte)',
      rich: 'Rich Cards',
      rich_media: 'Rich Media',
      sms: 'SMS Fallback'
    };
    return labels[category] || category;
  };

  const updateBillingForm = (category, field, value) => {
    setBillingForm(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: parseFloat(value) || 0
      }
    }));
  };

  if (user?.role !== 'superadmin') {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-500">Accès réservé au SuperAdmin</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="billing-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Facturation & Revenue</h1>
            <p className="text-gray-600 mt-1">
              Suivi du trafic, gestion des coûts Google et marges clients
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-40" data-testid="month-selector">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 12 }, (_, i) => {
                  const d = new Date();
                  d.setMonth(d.getMonth() - i);
                  const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                  return (
                    <SelectItem key={val} value={val}>
                      {d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              onClick={() => setExportModalOpen(true)}
              data-testid="export-billing-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Exporter
            </Button>
            <Button 
              variant="outline" 
              onClick={() => { fetchOverview(); fetchProfitSummary(); fetchInvoices(); }}
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* KPI Cards with Profit Info */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Coût Google</p>
                  <p className="text-2xl font-bold text-red-600" data-testid="google-cost-kpi">
                    {profitSummary?.summary?.total_google_cost?.toFixed(2) || '0.00'}€
                  </p>
                </div>
                <div className="p-3 bg-red-100 rounded-lg">
                  <ArrowDownRight className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Revenue Client</p>
                  <p className="text-2xl font-bold text-blue-600" data-testid="revenue-kpi">
                    {profitSummary?.summary?.total_revenue?.toFixed(2) || overview?.summary?.total_revenue?.toFixed(2) || '0.00'}€
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Euro className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Profit Net</p>
                  <p className="text-2xl font-bold text-green-600" data-testid="profit-kpi">
                    {profitSummary?.summary?.total_profit?.toFixed(2) || '0.00'}€
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <ArrowUpRight className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Marge Effective</p>
                  <p className="text-2xl font-bold text-purple-600" data-testid="margin-kpi">
                    {profitSummary?.summary?.margin_effective?.toFixed(1) || '0'}%
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Percent className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Messages Envoyés</p>
                  <p className="text-2xl font-bold text-indigo-600" data-testid="messages-kpi">
                    {overview?.summary?.total_messages?.toLocaleString() || '0'}
                  </p>
                </div>
                <div className="p-3 bg-indigo-100 rounded-lg">
                  <MessageSquare className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview" data-testid="tab-overview">Vue d&apos;ensemble</TabsTrigger>
            <TabsTrigger value="profits" data-testid="tab-profits">Profits par Client</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="tab-pricing">Tarification</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices">Factures</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Revenue by Client */}
              <Card>
                <CardHeader>
                  <CardTitle>Revenue par Client</CardTitle>
                  <CardDescription>{overview?.period_label}</CardDescription>
                </CardHeader>
                <CardContent>
                  {overview?.by_client?.length > 0 ? (
                    <div className="space-y-3">
                      {overview.by_client.slice(0, 10).map((client, idx) => (
                        <div key={client.client_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-gray-500">#{idx + 1}</span>
                            <div>
                              <p className="font-medium">{client.client_name || client.company_name}</p>
                              <p className="text-sm text-gray-500">
                                {client.total_messages.toLocaleString()} messages
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-600">{client.total_revenue.toFixed(2)}€</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-8">Aucune donnée pour cette période</p>
                  )}
                </CardContent>
              </Card>

              {/* Trend Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Évolution Mensuelle</CardTitle>
                  <CardDescription>6 derniers mois</CardDescription>
                </CardHeader>
                <CardContent>
                  {trend.length > 0 ? (
                    <div className="space-y-4">
                      {trend.map((month) => {
                        const maxRevenue = Math.max(...trend.map(t => t.revenue));
                        const percentage = maxRevenue > 0 ? (month.revenue / maxRevenue) * 100 : 0;
                        return (
                          <div key={month.month} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">{month.label}</span>
                              <span className="font-medium">{month.revenue.toFixed(2)}€</span>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-indigo-600 rounded-full transition-all"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <p className="text-xs text-gray-400">
                              {month.messages.toLocaleString()} messages
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-8">Aucune donnée disponible</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Profits Tab - NEW */}
          <TabsContent value="profits">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5" />
                  Analyse des Profits par Client
                </CardTitle>
                <CardDescription>
                  Détail coût Google vs revenue client avec calcul du profit
                </CardDescription>
              </CardHeader>
              <CardContent>
                {profitSummary?.by_client?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="profits-table">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4">Client</th>
                          <th className="text-right py-3 px-4">Messages</th>
                          <th className="text-right py-3 px-4">Coût Google</th>
                          <th className="text-right py-3 px-4">Revenue</th>
                          <th className="text-right py-3 px-4">Profit</th>
                          <th className="text-right py-3 px-4">Marge</th>
                        </tr>
                      </thead>
                      <tbody>
                        {profitSummary.by_client.map(client => (
                          <tr key={client.client_id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4">
                              <p className="font-medium">{client.client_name}</p>
                            </td>
                            <td className="text-right py-3 px-4">
                              {client.total_messages.toLocaleString()}
                            </td>
                            <td className="text-right py-3 px-4 text-red-600">
                              {client.total_google_cost.toFixed(2)}€
                            </td>
                            <td className="text-right py-3 px-4 text-blue-600">
                              {client.total_revenue.toFixed(2)}€
                            </td>
                            <td className="text-right py-3 px-4 font-medium text-green-600">
                              {client.total_profit.toFixed(2)}€
                            </td>
                            <td className="text-right py-3 px-4">
                              <Badge variant={client.margin_effective >= 50 ? "default" : "secondary"}>
                                {client.margin_effective.toFixed(1)}%
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50 font-medium">
                        <tr>
                          <td className="py-3 px-4">Total</td>
                          <td className="text-right py-3 px-4">
                            {profitSummary.by_client.reduce((sum, c) => sum + c.total_messages, 0).toLocaleString()}
                          </td>
                          <td className="text-right py-3 px-4 text-red-600">
                            {profitSummary.summary.total_google_cost.toFixed(2)}€
                          </td>
                          <td className="text-right py-3 px-4 text-blue-600">
                            {profitSummary.summary.total_revenue.toFixed(2)}€
                          </td>
                          <td className="text-right py-3 px-4 text-green-600">
                            {profitSummary.summary.total_profit.toFixed(2)}€
                          </td>
                          <td className="text-right py-3 px-4">
                            <Badge>{profitSummary.summary.margin_effective.toFixed(1)}%</Badge>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">Aucune donnée pour cette période</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pricing Tab - UPDATED */}
          <TabsContent value="pricing">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Configuration Coûts & Marges
                  </CardTitle>
                  <CardDescription>
                    Définissez le coût Google et la marge pour chaque type de message.
                    Le prix client est calculé automatiquement.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Sélectionner un client</Label>
                    <Select value={selectedClient} onValueChange={handleClientSelect}>
                      <SelectTrigger data-testid="client-selector">
                        <SelectValue placeholder="Choisir un client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map(client => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name} - {client.company_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedClient && (
                    <div className="space-y-4 pt-4 border-t">
                      {/* Header row */}
                      <div className="grid grid-cols-12 gap-2 text-sm font-medium text-gray-500 px-1">
                        <div className="col-span-3">Type</div>
                        <div className="col-span-3 text-center">Coût Google (€)</div>
                        <div className="col-span-3 text-center">Marge (%)</div>
                        <div className="col-span-3 text-center">Prix Client (€)</div>
                      </div>
                      
                      {Object.entries(billingForm).map(([category, config]) => {
                        const clientPrice = calculateClientPrice(config.google_cost, config.margin_percent);
                        const profit = clientPrice - config.google_cost;
                        
                        return (
                          <div key={category} className="grid grid-cols-12 gap-2 items-center p-3 bg-gray-50 rounded-lg">
                            <div className="col-span-3 flex items-center gap-2">
                              {getCategoryIcon(category)}
                              <span className="text-sm font-medium">{getCategoryLabel(category)}</span>
                            </div>
                            <div className="col-span-3">
                              <Input
                                type="number"
                                step="0.001"
                                min="0"
                                value={config.google_cost}
                                onChange={(e) => updateBillingForm(category, 'google_cost', e.target.value)}
                                className="text-center"
                                data-testid={`input-google-cost-${category}`}
                              />
                            </div>
                            <div className="col-span-3">
                              <Input
                                type="number"
                                step="1"
                                min="0"
                                max="500"
                                value={config.margin_percent}
                                onChange={(e) => updateBillingForm(category, 'margin_percent', e.target.value)}
                                className="text-center"
                                data-testid={`input-margin-${category}`}
                              />
                            </div>
                            <div className="col-span-3 text-center">
                              <div className="font-medium text-green-600">{clientPrice.toFixed(4)}€</div>
                              <div className="text-xs text-gray-500">+{profit.toFixed(4)}€/msg</div>
                            </div>
                          </div>
                        );
                      })}

                      <Button 
                        onClick={handleSaveBillingConfig} 
                        className="w-full bg-indigo-600 hover:bg-indigo-700"
                        disabled={savingConfig}
                        data-testid="save-billing-config-btn"
                      >
                        {savingConfig ? 'Enregistrement...' : 'Enregistrer la configuration'}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Configuration Actuelle</CardTitle>
                  <CardDescription>
                    {clientBillingConfig?.client_name 
                      ? `Configuration pour ${clientBillingConfig.client_name}`
                      : 'Sélectionnez un client pour voir sa configuration'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {clientBillingConfig ? (
                    <div className="space-y-3">
                      {Object.entries(clientBillingConfig.billing_config).map(([category, config]) => (
                        <div key={category} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            {getCategoryIcon(category)}
                            <span className="font-medium">{getCategoryLabel(category)}</span>
                          </div>
                          <div className="text-right space-y-1">
                            <div className="flex items-center gap-3 text-sm">
                              <span className="text-red-600">{config.google_cost.toFixed(3)}€</span>
                              <span className="text-gray-400">+</span>
                              <span className="text-purple-600">{config.margin_percent}%</span>
                              <span className="text-gray-400">=</span>
                              <span className="font-bold text-green-600">{config.client_price.toFixed(4)}€</span>
                            </div>
                            <div className="text-xs text-gray-500">
                              Profit: {config.profit_per_message.toFixed(4)}€/msg
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {clientBillingConfig.updated_at && (
                        <p className="text-xs text-gray-400 text-center pt-2">
                          Dernière mise à jour: {new Date(clientBillingConfig.updated_at).toLocaleString('fr-FR')}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-500 mb-4">
                        Configuration par défaut (appliquée si aucune config spécifique) :
                      </p>
                      {[
                        { key: 'basic', label: 'Basic (OTP)', cost: 0.005, margin: 60 },
                        { key: 'single', label: 'Single (Texte)', cost: 0.008, margin: 50 },
                        { key: 'rich', label: 'Rich Cards', cost: 0.015, margin: 66 },
                        { key: 'rich_media', label: 'Rich Media', cost: 0.030, margin: 50 },
                        { key: 'sms', label: 'SMS Fallback', cost: 0.040, margin: 37.5 }
                      ].map(item => {
                        const clientPrice = calculateClientPrice(item.cost, item.margin);
                        return (
                          <div key={item.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              {getCategoryIcon(item.key)}
                              <span>{item.label}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Badge variant="outline" className="text-red-600">{item.cost}€</Badge>
                              <span>+</span>
                              <Badge variant="outline" className="text-purple-600">{item.margin}%</Badge>
                              <span>=</span>
                              <Badge className="bg-green-600">{clientPrice.toFixed(4)}€</Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <Card>
              <CardHeader>
                <CardTitle>Historique des Factures</CardTitle>
              </CardHeader>
              <CardContent>
                {invoices.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="invoices-table">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4">N° Facture</th>
                          <th className="text-left py-3 px-4">Client</th>
                          <th className="text-left py-3 px-4">Période</th>
                          <th className="text-right py-3 px-4">Messages</th>
                          <th className="text-right py-3 px-4">Montant</th>
                          <th className="text-center py-3 px-4">Statut</th>
                          <th className="text-center py-3 px-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map(invoice => (
                          <tr key={invoice.invoice_number} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4 font-mono text-sm">{invoice.invoice_number}</td>
                            <td className="py-3 px-4">{invoice.client_name}</td>
                            <td className="py-3 px-4">{invoice.period_label}</td>
                            <td className="text-right py-3 px-4">
                              {invoice.total_messages.toLocaleString()}
                            </td>
                            <td className="text-right py-3 px-4 font-medium">
                              {invoice.total_cost.toFixed(2)}€
                            </td>
                            <td className="text-center py-3 px-4">
                              <Badge variant={invoice.status === 'paid' ? 'default' : 'secondary'}>
                                {invoice.status === 'paid' ? 'Payée' : 'Brouillon'}
                              </Badge>
                            </td>
                            <td className="text-center py-3 px-4">
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => handleDownloadPDF(invoice.client_id, invoice.period)}
                                data-testid={`download-pdf-${invoice.invoice_number}`}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">Aucune facture générée</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Export Modal */}
        <ExportModal 
          open={exportModalOpen} 
          onOpenChange={setExportModalOpen}
          defaultType="billing_report"
        />
      </div>
    </Layout>
  );
}
